import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus, Trash2, ChevronDown, ChevronUp, Check, Search,
  Shield, Mail, Laptop, Lock, Network, Wifi, Bug, Activity,
  Workflow, ScanLine, Layers, ShieldAlert, Cloud, Boxes,
} from "lucide-react";
import WipInterestDialog from "@/components/WipInterestDialog";

// Map server-provided icon name → lucide component
const ICONS = {
  Shield, Mail, Laptop, Lock, Network, Wifi, Bug, Activity,
  Workflow, ScanLine, Layers, ShieldAlert, Cloud,
};

const fmt = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD" }).format(v)
    : v;

/**
 * AddonsPanel — Lets the SE pick add-on Fortinet solutions (FortiWeb,
 * FortiMail, FortiEDR, …). Each solution is a card; clicking opens an
 * inline SKU picker driven by /api/solutions/{id}/skus. The selected
 * line items are merged into the BOM/BOQ via the `addons` field of
 * /api/configure.
 *
 * Props:
 *   value: [{sku, quantity, solution_id, name?, price?}]
 *   onChange: (newArray) => void
 *   useCase: current use_case (string) — used to highlight recommended solutions
 */
export default function AddonsPanel({ value, onChange, useCase, securityLevel, licenseYears, allowedSolutionIds, hiddenSolutionIds }) {
  const [open, setOpen] = useState(false);
  const [solutions, setSolutions] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // solution_id whose picker is open
  const [skus, setSkus] = useState({});             // { solution_id: {items, total, loading} }
  const [skuQuery, setSkuQuery] = useState("");
  const [fapRegions, setFapRegions] = useState([]);
  const [fapRegionCode, setFapRegionCode] = useState("E"); // default Europe
  // WIP pre-reservation dialog — set to the disabled solution that was clicked.
  const [wipFor, setWipFor] = useState(null); // {id, name} | null

  useEffect(() => {
    api.get("/solutions/catalog")
      .then((r) => setSolutions(r.data?.items || []))
      .catch(() => setSolutions([]));
    api.get("/solutions/fortiap/regions")
      .then((r) => setFapRegions(r.data?.items || []))
      .catch(() => setFapRegions([]));
  }, []);

  useEffect(() => {
    if (!useCase) return;
    api.get("/solutions/recommend", { params: { use_case: useCase } })
      .then((r) => setRecommended(r.data?.recommended || []))
      .catch(() => setRecommended([]));
  }, [useCase]);

  const ensureSkus = (id, q = "") => {
    const key = `${id}|${q}|${securityLevel || ""}|${licenseYears || ""}`;
    if (skus[key]) return;
    setSkus((p) => ({ ...p, [key]: { loading: true, items: [], total: 0 } }));
    api.get(`/solutions/${id}/skus`, {
      params: {
        q,
        limit: 30,
        security_level: securityLevel || "",
        license_years: licenseYears || 0,
      },
    })
      .then((r) => setSkus((p) => ({ ...p, [key]: { loading: false, items: r.data.items, total: r.data.total } })))
      .catch(() => setSkus((p) => ({ ...p, [key]: { loading: false, items: [], total: 0 } })));
  };

  const togglePicker = (id) => {
    if (pickerFor === id) {
      setPickerFor(null);
      return;
    }
    setPickerFor(id);
    setSkuQuery("");
    ensureSkus(id, "");
  };

  // Refetch when search query changes
  useEffect(() => {
    if (pickerFor) ensureSkus(pickerFor, skuQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuQuery, pickerFor]);

  const addLine = (solution_id, sku, name, price) => {
    let effectiveSku = sku;
    let effectiveName = name;
    // FortiAP: rewrite the regulatory suffix so the partner always orders the
    // correct country variant (ex. FAP-231K-A → FAP-231K-E for Europe).
    if (solution_id === "fortiap" && /^FAP-.+-[A-Z]$/.test(sku) && fapRegionCode) {
      const base = sku.replace(/-[A-Z]$/, "");
      effectiveSku = `${base}-${fapRegionCode}`;
      const regLabel = fapRegions.find((r) => r.code === fapRegionCode)?.label;
      effectiveName = regLabel ? `${name} — ${regLabel}` : name;
    }
    const existing = value.find((v) => v.sku === effectiveSku);
    if (existing) {
      onChange(value.map((v) => (v.sku === effectiveSku ? { ...v, quantity: Number(v.quantity) + 1 } : v)));
      toast.success(`${effectiveName} — quantité +1`);
    } else {
      onChange([...value, { solution_id, sku: effectiveSku, name: effectiveName, price, quantity: 1 }]);
      toast.success(`${effectiveName} ajouté au BOM`);
    }
    setPickerFor(null);
  };

  const removeLine = (sku) => onChange(value.filter((v) => v.sku !== sku));
  const setQty = (sku, qty) =>
    onChange(value.map((v) => (v.sku === sku ? { ...v, quantity: Math.max(1, Number(qty) || 1) } : v)));
  const setIncludePs = (sku, on) =>
    onChange(value.map((v) => (v.sku === sku ? { ...v, include_ps: !!on } : v)));

  // Fetch PS info per (solution_id, users, license_years)
  const [psBySolution, setPsBySolution] = useState({});
  const psKey = (sid) => `${sid}|${securityLevel || ""}|${licenseYears || 0}`;
  useEffect(() => {
    const ids = Array.from(new Set(value.map((v) => v.solution_id).filter(Boolean)));
    ids.forEach((sid) => {
      const k = psKey(sid);
      if (psBySolution[k]) return;
      api.get(`/solutions/${sid}/ps`, {
        params: { users: 0, license_years: licenseYears || 1 },
      })
        .then((r) => setPsBySolution((p) => ({ ...p, [k]: r.data })))
        .catch(() => setPsBySolution((p) => ({ ...p, [k]: { available: false } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, licenseYears, securityLevel]);

  const acceptRecommendations = async () => {
    const allowedReco = recommended.filter(isAllowed);
    if (allowedReco.length === 0) return;
    const next = [...value];
    for (const id of allowedReco) {
      // Use the first available SKU for that solution — preferentially a
      // bundle matching the user's current security_level + license_years.
      const r = await api.get(`/solutions/${id}/skus`, {
        params: {
          limit: 1,
          security_level: securityLevel || "",
          license_years: licenseYears || 0,
        },
      });
      const it = r.data?.items?.[0];
      if (it && !next.some((v) => v.sku === it.sku)) {
        const meta = solutions.find((s) => s.id === id);
        next.push({ solution_id: id, sku: it.sku, name: it.name, price: it.price, quantity: 1 });
        if (meta) toast.success(`${meta.name} ajouté (recommandation)`);
      }
    }
    onChange(next);
  };

  const total = value.reduce((s, l) => s + (Number(l.price) || 0) * Number(l.quantity || 0), 0);
  const grouped = useMemo(() => {
    const hidden = new Set(hiddenSolutionIds || []);
    const by = {};
    solutions
      .filter((s) => !hidden.has(s.id))
      .forEach((s) => {
        (by[s.category] ||= []).push(s);
      });
    return by;
  }, [solutions, hiddenSolutionIds]);

  const selectedIds = new Set(value.map((v) => v.solution_id).filter(Boolean));

  // When `allowedSolutionIds` is set (e.g. BOM Networking mode), only these
  // solution ids remain clickable. All others are visually disabled and tagged
  // « En cours de développement ».
  const isAllowed = (id) => !allowedSolutionIds || allowedSolutionIds.includes(id);

  return (
    <div
      className={`mt-6 border rounded-sm overflow-hidden ${
        value.length > 0 ? "border-zinc-300 bg-white" : "border-zinc-200 bg-white"
      }`}
      data-testid="addons-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50/50 transition-colors"
        data-testid="addons-panel-toggle"
      >
        <div className="flex items-center gap-3">
          <Boxes className="h-5 w-5 text-[#D42B1E]" />
          <div>
            <p className="text-sm font-semibold text-zinc-900">
              Solutions complémentaires
              {value.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-sm bg-zinc-900 text-white text-[10px] font-mono">
                  {value.length} ligne{value.length > 1 ? "s" : ""} · {fmt(total)}
                </span>
              )}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              FortiWeb, FortiMail, FortiEDR, FortiSIEM, FortiSOAR, FortiNAC, FortiAuthenticator, FortiADC, FortiDDoS, FortiCASB, FortiSandbox…
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="border-t border-zinc-200 p-5">
          {/* Selected lines */}
          {value.length > 0 && (
            <div className="mb-5 border border-zinc-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="addons-selected-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Solution / SKU</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Prix unit.</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 w-28">Qté</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Total</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {value.map((l, idx) => {
                    const psData = psBySolution[psKey(l.solution_id)];
                    const hasPs = psData?.available;
                    const isMandatoryPs = hasPs && psData?.mandatory;
                    return (
                    <React.Fragment key={`${l.sku}-${idx}`}>
                    <tr className="border-b border-zinc-100">
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-zinc-900 text-sm">{l.name || l.sku}</div>
                        <div className="font-mono text-[10px] text-zinc-400">{l.sku}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700">{fmt(l.price)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Input
                          type="number"
                          min={1}
                          value={l.quantity}
                          onChange={(e) => setQty(l.sku, e.target.value)}
                          className="rounded-sm h-8 text-right ml-auto max-w-[80px] text-xs"
                          data-testid={`addon-qty-${l.sku}`}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-sm text-zinc-900">{fmt((l.price || 0) * (l.quantity || 0))}</td>
                      <td className="px-1 text-center">
                        <Button variant="ghost" size="sm" onClick={() => removeLine(l.sku)} className="h-8 w-8 p-0 text-zinc-400 hover:text-red-600" data-testid={`addon-remove-${l.sku}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                    {hasPs && (
                      <tr className="border-b border-zinc-100 bg-zinc-50/40">
                        <td colSpan={5} className="px-3 py-2">
                          <div className="flex items-center justify-between gap-3 pl-6">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-sm font-medium uppercase tracking-wide ${
                                    isMandatoryPs ? "bg-[#D42B1E] text-white" : "bg-zinc-700 text-white"
                                  }`}
                                  data-testid={`ps-badge-${l.sku}`}
                                >
                                  {isMandatoryPs ? "Service obligatoire" : "Professional Services"}
                                </span>
                                <span className="text-xs font-medium text-zinc-800">{psData.label}</span>
                                <span className="text-[10px] text-zinc-400 font-mono">{psData.sku}</span>
                              </div>
                              <p className="mt-0.5 text-[11px] text-zinc-500">
                                {isMandatoryPs
                                  ? `Ajouté automatiquement au BOM · ${fmt(psData.price * (psData.quantity || 1))}`
                                  : `À ajouter au BOM si déploiement assisté · ${fmt(psData.price * (psData.quantity || 1))}`}
                              </p>
                            </div>
                            {!isMandatoryPs && (
                              <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer shrink-0" data-testid={`ps-toggle-${l.sku}`}>
                                <input
                                  type="checkbox"
                                  checked={!!l.include_ps}
                                  onChange={(e) => setIncludePs(l.sku, e.target.checked)}
                                  className="h-4 w-4 accent-[#D42B1E]"
                                />
                                <span>Inclure</span>
                              </label>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Recommendations bar */}
          {recommended.filter(isAllowed).length > 0 && (
            <div className="mb-5 border border-zinc-200 bg-zinc-50/50 rounded-sm p-3 flex items-center justify-between gap-3" data-testid="addons-reco-bar">
              <p className="text-xs text-zinc-700">
                <span className="font-semibold">Recommandations</span> pour <span className="font-mono">{useCase}</span> :{" "}
                {recommended
                  .filter(isAllowed)
                  .map((id) => solutions.find((s) => s.id === id)?.name)
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <Button
                type="button"
                onClick={acceptRecommendations}
                size="sm"
                className="bg-zinc-900 hover:bg-zinc-800 rounded-sm text-xs"
                data-testid="addons-reco-apply"
              >
                <Plus className="h-3 w-3 mr-1.5" /> Tout ajouter
              </Button>
            </div>
          )}

          {/* Solution catalog grouped by category */}
          <div className="space-y-5">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">{cat}</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((s) => {
                    const Icon = ICONS[s.icon] || Boxes;
                    const isReco = recommended.includes(s.id);
                    const isPicked = selectedIds.has(s.id);
                    const isOpen = pickerFor === s.id;
                    const allowed = isAllowed(s.id);
                    const skuKey = `${s.id}|${isOpen ? skuQuery : ""}|${securityLevel || ""}|${licenseYears || ""}`;
                    const skuData = skus[skuKey] || { items: [], loading: false };
                    return (
                      <div
                        key={s.id}
                        className={`border rounded-sm transition-all ${
                          !allowed
                            ? "border-zinc-200 bg-zinc-50/60 opacity-70"
                            : isOpen ? "border-zinc-900" : isPicked ? "border-zinc-300 bg-zinc-50/30" : "border-zinc-200"
                        }`}
                        data-testid={`addon-card-${s.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (allowed) togglePicker(s.id);
                            else setWipFor({ id: s.id, name: s.name });
                          }}
                          title={allowed ? undefined : "Module en cours de développement — cliquez pour signaler votre intérêt"}
                          className={`w-full text-left p-3 flex items-start gap-3 rounded-sm transition-colors ${
                            allowed ? "hover:bg-zinc-50/50" : "hover:bg-amber-50/40 cursor-pointer"
                          }`}
                          data-testid={`addon-toggle-${s.id}`}
                        >
                          <div className={`shrink-0 h-9 w-9 rounded-sm flex items-center justify-center ${
                            !allowed ? "bg-zinc-100 text-zinc-400"
                              : isPicked ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"
                          }`}>
                            {isPicked && allowed ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`text-sm font-semibold truncate ${allowed ? "text-zinc-900" : "text-zinc-500 line-through decoration-zinc-300"}`}>
                                {s.name}
                              </p>
                              {isReco && allowed && (
                                <span className="text-[9px] px-1.5 py-0.5 bg-[#D42B1E] text-white rounded-sm font-medium uppercase tracking-wide">Reco</span>
                              )}
                              {!allowed && (
                                <span className="text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                                  En cours de développement
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-zinc-500 leading-snug mt-0.5 line-clamp-2">{s.description}</p>
                          </div>
                        </button>

                        {isOpen && allowed && (
                          <div className="border-t border-zinc-200 p-3 bg-white" data-testid={`addon-picker-${s.id}`}>
                            {s.id === "fortiap" && fapRegions.length > 0 && (
                              <div className="mb-2 border border-amber-200 bg-amber-50/60 rounded-sm px-3 py-2" data-testid="fap-region-selector">
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                  Région réglementaire FortiAP *
                                </Label>
                                <select
                                  value={fapRegionCode}
                                  onChange={(e) => setFapRegionCode(e.target.value)}
                                  className="mt-1 w-full h-8 text-xs bg-white border border-amber-300 rounded-sm px-2"
                                  data-testid="fap-region-select"
                                >
                                  {Object.entries(
                                    fapRegions.reduce((acc, r) => {
                                      (acc[r.region || "Autre"] ||= []).push(r);
                                      return acc;
                                    }, {})
                                  )
                                    .sort(([a], [b]) => {
                                      const order = { "EMEA": 0, "APAC": 1, "Americas": 2, "Americas/APAC": 3, "Générique": 9 };
                                      return (order[a] ?? 5) - (order[b] ?? 5);
                                    })
                                    .map(([grp, items]) => (
                                    <optgroup key={grp} label={grp}>
                                      {items.map((r) => (
                                        <option key={r.code} value={r.code}>
                                          {r.code} · {r.label}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                                <p className="mt-1 text-[10px] text-amber-900/80 leading-snug">
                                  Le suffixe SKU sera réécrit automatiquement (ex. FAP-231K → FAP-231K-{fapRegionCode}).
                                </p>
                              </div>
                            )}
                            <div className="relative mb-2">
                              <Search className="h-3 w-3 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                              <Input
                                autoFocus
                                placeholder="Filtrer par modèle (ex. 100F, VM)…"
                                value={skuQuery}
                                onChange={(e) => setSkuQuery(e.target.value)}
                                className="pl-7 rounded-sm h-8 text-xs"
                                data-testid={`addon-picker-search-${s.id}`}
                              />
                            </div>
                            <div className="max-h-56 overflow-y-auto divide-y divide-zinc-100 -mx-1">
                              {skuData.loading && <div className="p-2 text-xs text-zinc-500">Chargement…</div>}
                              {!skuData.loading && skuData.items.length === 0 && (
                                <div className="p-2 text-xs text-zinc-500">Aucun résultat.</div>
                              )}
                              {!skuData.loading && skuData.items.map((it) => (
                                <button
                                  key={it.sku}
                                  type="button"
                                  onClick={() => addLine(s.id, it.sku, it.name, it.price)}
                                  className={`w-full px-2 py-1.5 text-left flex items-start justify-between gap-2 hover:bg-zinc-50 rounded-sm ${
                                    it.recommended ? "bg-red-50/50" : ""
                                  }`}
                                  data-testid={`addon-pick-${s.id}-${it.sku}`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-[10px] text-zinc-500">{it.sku}</span>
                                      {it.recommended && (
                                        <span className="text-[8px] px-1 py-0.5 bg-[#D42B1E] text-white rounded-sm font-medium uppercase tracking-wide">
                                          Reco
                                        </span>
                                      )}
                                      {it.sku.includes("-BDL-") && (
                                        <span className="text-[8px] px-1 py-0.5 bg-zinc-200 text-zinc-700 rounded-sm font-mono">
                                          {it.sku.endsWith("-12") ? "1Y" : it.sku.endsWith("-36") ? "3Y" : it.sku.endsWith("-60") ? "5Y" : ""}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-zinc-900 line-clamp-1">{it.name}</div>
                                  </div>
                                  <div className="font-mono text-xs text-zinc-700 whitespace-nowrap">{fmt(it.price)}</div>
                                </button>
                              ))}
                            </div>
                            {skuData.total > skuData.items.length && (
                              <p className="mt-2 text-[10px] text-zinc-400 text-center">
                                {skuData.total} résultats au total — affinez la recherche.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pre-reservation dialog for WIP solutions — opens when a partner
          clicks a greyed-out card. Records interest signal in MongoDB. */}
      <WipInterestDialog
        open={!!wipFor}
        onClose={() => setWipFor(null)}
        solutionId={wipFor?.id}
        solutionName={wipFor?.name}
      />
    </div>
  );
}
